import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useMuvelet } from '../felhasznalo-context.ts';

import {
  api,
  type BankAllapot,
  type BankJeloltek,
  type BankTetelNezet,
  type KartyasNap,
  type SzamlaJelolt,
} from '../api.ts';
import { ft } from '../formazas.ts';
import { letolt } from '../letoltes.ts';

/**
 * Bankkivonatok és párosítás. (Megbízás 2.0 / 5.5)
 *
 * A feldolgozás három csoportba osztja a jóváírásokat:
 *
 *  - **Biztos párosítások** — a program megmondja, mi alapján döntött.
 *  - **Tisztázandók** — a probléma pontos megnevezésével. Nem tippelünk:
 *    egy rossz párosítás kifizetettnek jelölne egy számlát, amit nem
 *    fizettek ki, és az sokkal drágább hiba, mint egy kézi döntés.
 *  - **Technikai tételek** — nem ügyfélfizetés, figyelmen kívül hagyjuk.
 *
 * A tisztázandók kézzel párosíthatók; a párosítás és a technikai jelölés
 * visszavonható. Amiről ember döntött, azt az automatika nem veszi elő.
 *
 * Fent az **egyenleg-ellenőrzés**: a kivonat jóváírásainak ki kell adniuk a
 * három csoport összegét. Ha nem áll ki, az az első, amit meg kell nézni.
 */
export function Bankkivonatok() {
  /** Letöltés csak engedéllyel. (3.0 / 1.) */
  const letolthet = useMuvelet('export_penzugy');
  const [adat, setAdat] = useState<BankAllapot | null>(null);
  const [kartyas, setKartyas] = useState<KartyasNap[]>([]);
  const [ful, setFul] = useState<'parositas' | 'kartyas'>('parositas');
  /** A választott hónap; üresen a szerver a legutóbbit adja, `mind` = minden hónap. */
  const [valasztottHonap, setValasztottHonap] = useState('');
  const [hiba, setHiba] = useState<string | null>(null);
  const [uzenet, setUzenet] = useState<string | null>(null);
  const [munkaban, setMunkaban] = useState(false);
  const [nyitottNap, setNyitottNap] = useState<string | null>(null);
  const [nyitottTomb, setNyitottTomb] = useState<string | null>(null);

  const betolt = useCallback(async () => {
    try {
      const [a, k] = await Promise.all([
        api.get<BankAllapot>(`/api/bank?honap=${encodeURIComponent(valasztottHonap)}`),
        api.get<{ napok: KartyasNap[] }>('/api/bank/kartyas'),
      ]);
      setAdat(a);
      setKartyas(k.napok);
      setHiba(null);
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'Nem sikerült betölteni.');
    }
  }, [valasztottHonap]);

  useEffect(() => {
    void betolt();
  }, [betolt]);

  /**
   * A fájlok feltöltése. Több fájl egyszerre: a neveket és a hosszakat
   * a tartalom elé fűzzük, így nem kell külön feltöltő könyvtár.
   */
  async function feltolt(fajlok: FileList) {
    setMunkaban(true);
    setHiba(null);
    setUzenet(null);
    try {
      const darabok: BlobPart[] = [];
      for (const f of Array.from(fajlok)) {
        const nev = new TextEncoder().encode(f.name);
        const tartalom = new Uint8Array(await f.arrayBuffer());
        darabok.push(new TextEncoder().encode(`${nev.length}|`), nev);
        darabok.push(new TextEncoder().encode(`|${tartalom.length}|`), tartalom);
      }
      const test = new Blob(darabok);
      const valasz = await fetch('/api/bank/feltoltes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/octet-stream' },
        body: test,
      });
      const j = (await valasz.json()) as BankAllapot & { hiba?: string; figyelmeztetesek?: string[] };
      if (!valasz.ok) throw new Error(j.hiba ?? 'A feldolgozás nem sikerült.');

      setUzenet(
        `${j.kivonatok.length} kivonat feldolgozva · ${j.parositva.length} párosítva · ` +
          `${j.tisztazando.length} tisztázandó`,
      );
      if (j.figyelmeztetesek?.length) setHiba(j.figyelmeztetesek.join(' '));
      await betolt();
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'A feltöltés nem sikerült.');
    } finally {
      setMunkaban(false);
    }
  }

  async function technikainak(t: BankTetelNezet) {
    try {
      await api.post(`/api/bank/tetel/${t.id}/technikai`);
      setUzenet(`Technikainak jelölve: ${t.partner_nev ?? '—'} · ${ft(t.osszeg)} Ft. Visszavonható.`);
      await betolt();
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'A művelet nem sikerült.');
    }
  }

  async function visszavon(t: BankTetelNezet) {
    const mit = t.allapot === 'technikai' ? 'technikai jelölését' : 'párosítását';
    if (
      !confirm(
        `Visszavonod ennek a tételnek a ${mit}?\n\n${t.partner_nev ?? '—'} · ${ft(t.osszeg)} Ft\n\n` +
          'A tétel visszakerül a tisztázandók közé, a számlái újra nyitottak lesznek, és ' +
          'onnan kézzel párosíthatod. Az automatikus párosítás ezután nem nyúl hozzá. ' +
          'Ha túlfizetés is keletkezett belőle, az is törlődik.',
      )
    )
      return;
    try {
      await api.post(`/api/bank/tetel/${t.id}/visszavonas`);
      setUzenet('Visszavonva — a tétel a tisztázandók közt vár kézi döntésre.');
      await betolt();
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'A visszavonás nem sikerült.');
    }
  }

  async function kivonatTorol(k: BankAllapot['kivonatok'][number]) {
    if (
      !confirm(
        `Törlöd a(z) „${k.fajlnev}” kivonatot?\n\n` +
          `A tételei (${k.tranzakcio_db} db) törlődnek, és az általa kifizetett számlák újra ` +
          'nyitottak lesznek. Ha kézzel párosított tétel van benne, a program nem engedi — ' +
          'előbb azt kell visszavonni.\n\nA fájl utána újra betölthető.',
      )
    )
      return;
    try {
      const v = await api.post<{ fajlnev: string; tetelek: number; ujranyitott: number }>(
        `/api/bank/kivonat/${k.id}/torles`,
      );
      setUzenet(
        `Törölve: ${v.fajlnev} — ${v.tetelek} tétel` +
          (v.ujranyitott > 0 ? `, ${v.ujranyitott} számla újra nyitott.` : '.'),
      );
      await betolt();
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'A törlés nem sikerült.');
    }
  }

  async function ujraParosit() {
    setMunkaban(true);
    try {
      const v = await api.post<{ parositva: number }>('/api/bank/parositas');
      setUzenet(`Újrapárosítás kész: ${v.parositva} tétel párosult.`);
      await betolt();
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'Az újrapárosítás nem sikerült.');
    } finally {
      setMunkaban(false);
    }
  }

  const e = adat?.egyenleg;
  const honapFelirat = adat?.honap ? ` — ${honapNev(adat.honap)}` : '';

  return (
    <>
      <div className="dayhead">
        <span style={{ fontSize: 15, fontWeight: 600 }}>Bankkivonatok</span>
        <div className="vsw">
          <button className={ful === 'parositas' ? 'on' : ''} onClick={() => setFul('parositas')}>
            Párosítás
          </button>
          <button className={ful === 'kartyas' ? 'on' : ''} onClick={() => setFul('kartyas')}>
            Kártyás napi bontás
          </button>
        </div>
        {(adat?.honapok?.length ?? 0) > 0 && (
          <select
            className="qi"
            style={{ width: 190, fontSize: 12.5 }}
            value={adat?.honap ?? 'mind'}
            title="A párosított és technikai tételek, a kivonatok és a kártyás bontás ebből a hónapból látszanak. A tisztázandók mindig mind."
            onChange={(ev) => setValasztottHonap(ev.target.value)}
          >
            {(adat?.honapok ?? []).map((h) => (
              <option key={h.honap} value={h.honap}>
                {honapNev(h.honap)} ({h.tetelek} tétel)
              </option>
            ))}
            <option value="mind">minden hónap</option>
          </select>
        )}
        <span className="hint">
          CAMT.053 (XML) vagy MT940 (.KIV). Több fájl egyszerre is feltölthető.
        </span>
      </div>

      {/* Egyenleg-ellenőrzés — ez az első, amit meg kell nézni. */}
      {e && (adat?.kivonatok.length ?? 0) > 0 && (
        <div className={`uzenet ${e.rendben ? 'ok' : 'bad'}`}>
          <b>Egyenleg-ellenőrzés (minden kivonat):</b> a kivonatok jóváírásai {ft(e.kivonat_jovairasai)} Ft ={' '}
          {ft(e.technikai)} technikai + {ft(e.parositva)} párosított + {ft(e.tisztazando)}{' '}
          tisztázandó
          {e.rendben ? ' — kiadja.' : ` — ${ft(e.elteres)} Ft nem áll ki!`}
        </div>
      )}

      {hiba && (
        <div className="uzenet flag" onClick={() => setHiba(null)}>
          {hiba}
        </div>
      )}
      {uzenet && (
        <div className="uzenet ok" onClick={() => setUzenet(null)}>
          {uzenet}
        </div>
      )}

      <div className="actbar">
        <label className="btn pri sm" style={{ cursor: 'pointer', margin: 0 }}>
          Kivonat(ok) feltöltése
          <input
            type="file"
            multiple
            accept=".xml,.kiv,.KIV,.txt"
            style={{ display: 'none' }}
            onChange={(ev) => {
              const f = ev.target.files;
              if (f && f.length > 0) void feltolt(f);
              ev.target.value = '';
            }}
          />
        </label>
        <button className="btn sm" disabled={munkaban} onClick={() => void ujraParosit()}>
          Újrapárosítás
        </button>
        {letolthet && (
          <button
            className="btn sm"
            title="A kártyás elszámolások napi bontása Excelben: kivonat → tömb → számlák, a még el nem számolt kártyás számlákkal együtt"
            onClick={() => letolt('/api/export/kartyas-napi-bontas.xlsx')}
          >
            Kártyás napi bontás — Excel
          </button>
        )}
        {munkaban && <span className="hint">Dolgozom…</span>}
        <span className="hint">
          Az újrapárosítás akkor hasznos, ha időközben új számlák érkeztek — a korábban
          tisztázatlan tételek ilyenkor már párosulhatnak. A kézzel eldöntött tételekhez nem nyúl.
        </span>
      </div>

      {(adat?.kivonatok.length ?? 0) > 0 && (
        <div className="matrix" style={{ marginBottom: 14 }}>
          <table className="tt2">
            <thead>
              <tr>
                <th>Kivonat</th>
                <th>Formátum</th>
                <th className="n">Nap</th>
                <th className="n">Tétel</th>
                <th className="n">Jóváírás</th>
                <th style={{ width: 80 }}></th>
              </tr>
            </thead>
            <tbody>
              {(adat?.kivonatok ?? []).map((k) => (
                <tr key={k.id}>
                  <td className="nm3">
                    <b>{k.fajlnev}</b>
                  </td>
                  <td style={{ padding: '6px 10px' }}>
                    {k.formatum === 'camt' ? 'CAMT.053' : 'MT940'}
                  </td>
                  <td className="calc2">{k.kivonat_nap ?? '—'}</td>
                  <td className="calc2">{k.tranzakcio_db}</td>
                  <td className="calc2">{ft(k.jovairas_osszeg ?? 0)}</td>
                  <td className="act">
                    <button
                      className="btn sm"
                      title="Tévedésből betöltött kivonat törlése — az általa kifizetett számlák újra nyitottak lesznek"
                      onClick={() => void kivonatTorol(k)}
                    >
                      Törlés
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {ful === 'parositas' ? (
        <>
          <Csoport
            cim="Tisztázandók — minden hónap"
            szin="flag"
            magyarazat="Nem egyértelmű — itt ember dönt. A pontos ok minden sornál ott áll; a „Párosítás…” gombbal kézzel is hozzárendelhető."
            tetelek={adat?.tisztazando ?? []}
            onTechnikai={technikainak}
            onFrissit={betolt}
          />
          <Csoport
            cim={`Biztos párosítások${honapFelirat}`}
            szin="ok"
            magyarazat="A program megmondja, mi alapján döntött. Ha rossz, visszavonható."
            tetelek={adat?.parositva ?? []}
            onVisszavon={visszavon}
          />
          <Csoport
            cim={`Technikai tételek${honapFelirat}`}
            szin="halvany"
            magyarazat="Nem ügyfélfizetés: átvezetés, banki díj, NAV, ATM. A terhelések eleve kimaradnak. A kézi jelölés visszavonható."
            tetelek={adat?.technikai ?? []}
            onVisszavon={visszavon}
          />
        </>
      ) : (
        <KartyasBontas
          napok={adat?.honap ? kartyas.filter((n) => n.nap.startsWith(adat.honap!)) : kartyas}
          nyitottNap={nyitottNap}
          nyitottTomb={nyitottTomb}
          onNap={(n) => setNyitottNap((e2) => (e2 === n ? null : n))}
          onTomb={(t) => setNyitottTomb((e2) => (e2 === t ? null : t))}
        />
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */

const HONAPOK = [
  'január', 'február', 'március', 'április', 'május', 'június',
  'július', 'augusztus', 'szeptember', 'október', 'november', 'december',
];

/** `2026-09` → `2026. szeptember` */
function honapNev(honap: string): string {
  const [ev, ho] = honap.split('-');
  return `${ev}. ${HONAPOK[Number(ho) - 1] ?? ho}`;
}

function Csoport({
  cim,
  szin,
  magyarazat,
  tetelek,
  onTechnikai,
  onVisszavon,
  onFrissit,
}: {
  cim: string;
  szin: 'ok' | 'flag' | 'halvany';
  magyarazat: string;
  tetelek: BankTetelNezet[];
  onTechnikai?: (t: BankTetelNezet) => void;
  onVisszavon?: (t: BankTetelNezet) => void;
  /** Kézi párosítás után az egész képernyő frissül. */
  onFrissit?: () => Promise<void>;
}) {
  const [nyitott, setNyitott] = useState<number | null>(null);
  const osszeg = tetelek.reduce((a, t) => a + t.osszeg, 0);
  const vanMuvelet = !!(onTechnikai || onVisszavon);
  // Terhelésnél nincs mit visszavonni: az mindig technikai.
  const visszavonhato = (t: BankTetelNezet) => t.irany === 'jovairas';

  return (
    <>
      <p className="lbl" style={{ marginTop: 16 }}>
        {cim} <b>{tetelek.length}</b>
        {tetelek.length > 0 && (
          <span style={{ marginLeft: 8, fontFamily: 'var(--mono)' }}>{ft(osszeg)} Ft</span>
        )}
      </p>
      <p className="hint" style={{ margin: '0 0 6px' }}>
        {magyarazat}
      </p>

      {tetelek.length === 0 ? (
        <div className="uzenet halvany" style={{ marginTop: 0 }}>
          Nincs ilyen tétel.
        </div>
      ) : (
        <div className="matrix" style={{ maxHeight: 520, overflow: 'auto' }}>
          <table className="tt2">
            <thead>
              <tr>
                <th className="n" style={{ width: 92 }}>
                  Értéknap
                </th>
                <th style={{ width: 200 }}>Befizető</th>
                <th className="n" style={{ width: 100 }}>
                  Összeg
                </th>
                <th style={{ width: 170 }}>Közlemény</th>
                <th>{szin === 'flag' ? 'Mi a baj' : 'Mi alapján / mi ez'}</th>
                {vanMuvelet && <th style={{ width: onTechnikai ? 190 : 100 }}></th>}
              </tr>
            </thead>
            <tbody>
              {tetelek.map((t) => (
                <Fragment key={t.id}>
                  <tr>
                    <td className="calc2">{t.ertek_nap ?? '—'}</td>
                    <td style={{ padding: '6px 10px', fontSize: 11.5 }}>
                      {t.partner_nev ?? <span className="halvany">—</span>}
                    </td>
                    <td className="calc2" style={{ fontWeight: 600 }}>
                      {ft(t.osszeg)}
                      {t.pos_brutto !== null && (
                        <div className="halvany" style={{ fontWeight: 400, fontSize: 10.5 }}>
                          bruttó {ft(t.pos_brutto)}
                        </div>
                      )}
                    </td>
                    <td
                      style={{
                        padding: '6px 10px',
                        fontSize: 11,
                        fontFamily: 'var(--mono)',
                        color: 'var(--muted)',
                      }}
                    >
                      {t.kozlemeny ?? '—'}
                    </td>
                    <td style={{ padding: '6px 10px', fontSize: 11.5 }}>
                      <span className={szin === 'flag' ? 'lejar-hamarosan' : undefined}>
                        {t.indoklas ?? '—'}
                      </span>
                      {t.kezi_dontes && szin !== 'flag' && (
                        <span className="halvany" style={{ marginLeft: 6 }}>
                          (kézi)
                        </span>
                      )}
                      {t.szamlak.length > 0 && (
                        <div style={{ marginTop: 3, fontFamily: 'var(--mono)', fontSize: 11 }}>
                          {t.szamlak.map((s) => s.bizonylatszam).join(' · ')}
                        </div>
                      )}
                      {t.tulfizetes && t.tulfizetes.maradek > 2 && (
                        <div className="halvany" style={{ marginTop: 3, fontSize: 11 }}>
                          túlfizetésből még felhasználható {ft(t.tulfizetes.maradek)} Ft — Számlák →
                          Túlfizetések
                        </div>
                      )}
                    </td>
                    {vanMuvelet && (
                      <td className="act" style={{ whiteSpace: 'nowrap' }}>
                        {onTechnikai && (
                          <>
                            <button
                              className={`btn sm${nyitott === t.id ? ' pri' : ''}`}
                              title="Számla (vagy számlák) kézi hozzárendelése"
                              onClick={() => setNyitott((n) => (n === t.id ? null : t.id))}
                            >
                              Párosítás…
                            </button>{' '}
                            <button
                              className="btn sm"
                              title="Nem ügyfélfizetés — kerüljön a technikai tételek közé. Visszavonható."
                              onClick={() => onTechnikai(t)}
                            >
                              Technikai
                            </button>
                          </>
                        )}
                        {onVisszavon && visszavonhato(t) && (
                          <button
                            className="btn sm"
                            title="A tétel visszakerül a tisztázandók közé, a számlái újra nyitottak lesznek."
                            onClick={() => onVisszavon(t)}
                          >
                            Visszavonás
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                  {nyitott === t.id && (
                    <tr>
                      <td colSpan={6} style={{ padding: 0 }}>
                        <KeziParosito
                          tetel={t}
                          onMegse={() => setNyitott(null)}
                          onKesz={async () => {
                            setNyitott(null);
                            await onFrissit?.();
                          }}
                        />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */

/**
 * Kézi párosítás egy tisztázandó tételhez.
 *
 * Elöl a program javaslata (előre kijelölve) és a jelöltek — a befizető cég
 * nyitott számlái, kártyásnál a nap számlái —, fölöttük kereső bármely más
 * nyitott számlához. Alul azonnal látszik, mennyi a kijelöltek összege és
 * mennyi az eltérés a befizetéstől.
 */
function KeziParosito({
  tetel,
  onKesz,
  onMegse,
}: {
  tetel: BankTetelNezet;
  onKesz: () => Promise<void>;
  onMegse: () => void;
}) {
  const [adat, setAdat] = useState<BankJeloltek | null>(null);
  const [ismert, setIsmert] = useState<Map<number, SzamlaJelolt>>(new Map());
  const [kijelolt, setKijelolt] = useState<Set<number>>(new Set());
  const [kereses, setKereses] = useState('');
  const [talalatok, setTalalatok] = useState<SzamlaJelolt[]>([]);
  const [hiba, setHiba] = useState<string | null>(null);
  const [ment, setMent] = useState(false);
  /** A túlfizetés cége, ha a felhasználó átírta; addig a javaslat áll benne. */
  const [tulCegKezi, setTulCegKezi] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<BankJeloltek>(`/api/bank/tetel/${tetel.id}/jeloltek`)
      .then((j) => {
        setAdat(j);
        setIsmert(new Map(j.jeloltek.map((s) => [s.id, s])));
        setKijelolt(new Set(j.javasolt));
      })
      .catch((h: unknown) => setHiba(h instanceof Error ? h.message : 'A jelöltek nem tölthetők be.'));
  }, [tetel.id]);

  useEffect(() => {
    const q = kereses.trim();
    if (q.length < 2) {
      setTalalatok([]);
      return;
    }
    const idozito = setTimeout(async () => {
      try {
        const v = await api.get<{ talalatok: SzamlaJelolt[] }>(
          `/api/bank/szamlak?q=${encodeURIComponent(q)}`,
        );
        setTalalatok(v.talalatok);
        setIsmert((m) => {
          const uj = new Map(m);
          for (const s of v.talalatok) uj.set(s.id, s);
          return uj;
        });
      } catch {
        setTalalatok([]);
      }
    }, 250);
    return () => clearTimeout(idozito);
  }, [kereses]);

  // A kijelöltek mindig látszanak, utánuk a jelöltek, végül a keresés találatai.
  const lista = useMemo(() => {
    const sorrend: SzamlaJelolt[] = [];
    const lattuk = new Set<number>();
    const tesz = (s: SzamlaJelolt | undefined) => {
      if (s && !lattuk.has(s.id)) {
        lattuk.add(s.id);
        sorrend.push(s);
      }
    };
    for (const id of kijelolt) tesz(ismert.get(id));
    for (const s of adat?.jeloltek ?? []) tesz(s);
    for (const s of talalatok) tesz(s);
    return sorrend;
  }, [kijelolt, ismert, adat, talalatok]);

  const cel = adat?.cel ?? tetel.pos_brutto ?? tetel.osszeg;
  // A számla hátraléka számít: részfizetés után csak a még fizetendő rész.
  const osszeg = [...kijelolt].reduce((a, id) => a + (ismert.get(id)?.hatralek ?? 0), 0);
  const elteres = Math.round((cel - osszeg) * 100) / 100;
  const kiall = kijelolt.size > 0 && Math.abs(elteres) <= 2 * kijelolt.size;

  /**
   * Túlfizetés: ami a kijelölt számlák után megmarad (számla nélkül a teljes
   * összeg), a céghez kerül. Alapból a kijelölt számlák cége, számla nélkül
   * a felismert befizető — átírható.
   */
  const tulfizetesLehet = adat?.tulfizetes_lehet ?? tetel.pos_brutto === null;
  const vanTulfizetes = tulfizetesLehet && elteres > 2 * Math.max(1, kijelolt.size);
  const kijeloltCegek = [
    ...new Set([...kijelolt].map((id) => ismert.get(id)?.cegnev).filter((c): c is string => !!c)),
  ];
  const alapCeg =
    kijelolt.size > 0
      ? kijeloltCegek.length === 1
        ? kijeloltCegek[0]!
        : ''
      : (adat?.ceg_javaslat ?? tetel.partner_nev ?? '');
  const tulCeg = tulCegKezi ?? alapCeg;
  const tovabbiNyitott = (adat?.jeloltek ?? []).filter((s) => s.nyitott && !kijelolt.has(s.id)).length;

  function valt(id: number) {
    setKijelolt((k) => {
      const uj = new Set(k);
      if (uj.has(id)) uj.delete(id);
      else uj.add(id);
      return uj;
    });
  }

  async function rogzit() {
    setMent(true);
    setHiba(null);
    try {
      await api.post(`/api/bank/tetel/${tetel.id}/parositas`, {
        szamla_idk: [...kijelolt],
        tulfizetes_ceg: vanTulfizetes ? tulCeg : null,
      });
      await onKesz();
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'A párosítás nem sikerült.');
    } finally {
      setMent(false);
    }
  }

  return (
    <div style={{ padding: '10px 12px', background: 'var(--bluew)', borderTop: '1.5px solid var(--blue)', borderBottom: '1.5px solid var(--blue)' }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <b>Kézi párosítás</b>
        <span className="hint">
          A számláknak {ft(cel)} Ft-ot kell kiadniuk
          {tetel.pos_brutto !== null ? ' (a kártyás elszámolás bruttója)' : ''}.
        </span>
        <input
          className="qi"
          style={{ width: 320, fontSize: 12.5, marginLeft: 'auto' }}
          placeholder="más számla keresése: szám, cégnév, telephely vagy összeg…"
          value={kereses}
          onChange={(e) => setKereses(e.target.value)}
        />
      </div>
      {adat?.megjegyzes && (
        <div className="hint" style={{ margin: '6px 0 0' }}>
          {adat.megjegyzes}
        </div>
      )}
      {hiba && <div className="uzenet bad">{hiba}</div>}

      <div className="matrix" style={{ marginTop: 8, maxHeight: 300, overflow: 'auto', background: '#fff' }}>
        <table className="tt2">
          <thead>
            <tr>
              <th style={{ width: 30 }}></th>
              <th style={{ width: 90 }}>Számla</th>
              <th>Cégnév / telephely</th>
              <th className="n" style={{ width: 92 }}>
                Kelt
              </th>
              <th style={{ width: 100 }}>Fiz. mód</th>
              <th className="n" style={{ width: 100 }}>
                Bruttó
              </th>
              <th className="n" style={{ width: 100 }}>
                Hátralék
              </th>
              <th style={{ width: 150 }}>Állapot</th>
            </tr>
          </thead>
          <tbody>
            {lista.map((s) => (
              <tr
                key={s.id}
                style={{ cursor: s.nyitott ? 'pointer' : 'default', opacity: s.nyitott ? 1 : 0.55 }}
                onClick={() => s.nyitott && valt(s.id)}
              >
                <td style={{ textAlign: 'center' }}>
                  <input
                    type="checkbox"
                    checked={kijelolt.has(s.id)}
                    disabled={!s.nyitott}
                    onChange={() => valt(s.id)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </td>
                <td className="calc2" style={{ textAlign: 'left', fontWeight: 600 }}>
                  {s.bizonylatszam}
                </td>
                <td style={{ padding: '5px 10px', fontSize: 11.5 }}>
                  {s.cegnev ?? '—'}
                  {s.telephely_nev && <span className="halvany"> · {s.telephely_nev}</span>}
                </td>
                <td className="calc2">{s.kelt ?? '—'}</td>
                <td style={{ padding: '5px 10px', fontSize: 11.5 }}>{s.fizetesi_mod ?? '—'}</td>
                <td className="calc2">{ft(s.brutto)}</td>
                <td className="calc2" style={{ fontWeight: s.nyitott ? 600 : 400 }}>
                  {s.nyitott ? ft(s.hatralek) : '—'}
                </td>
                <td style={{ padding: '5px 10px', fontSize: 11.5 }}>
                  {s.nyitott
                    ? s.fizetett > 0
                      ? <span style={{ color: 'var(--flag)' }}>részben fizetve ({ft(s.fizetett)} Ft)</span>
                      : 'nyitott'
                    : s.jovairas_datum
                      ? `kifizetve ${s.jovairas_datum}`
                      : 'nem fizethető'}
                </td>
              </tr>
            ))}
            {lista.length === 0 && (
              <tr>
                <td colSpan={8} style={{ padding: 16, textAlign: 'center', color: 'var(--muted)' }}>
                  {adat ? 'Nincs felkínálható számla — keress rá fent szám, név vagy összeg szerint.' : 'Betöltés…'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {vanTulfizetes && (
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 8, flexWrap: 'wrap' }}>
          <span className="lbl" style={{ margin: 0 }}>
            Túlfizetés cége
          </span>
          <input
            className="qi"
            style={{ width: 300, fontSize: 12.5 }}
            value={tulCeg}
            placeholder="melyik céghez kerüljön a túlfizetés"
            onChange={(e) => setTulCegKezi(e.target.value)}
          />
          <span className="hint">
            A Számlák → Túlfizetések fülön látszik, és onnan később számlához párosítható.
          </span>
          {tovabbiNyitott > 0 && (
            <span style={{ color: 'var(--flag)', fontWeight: 600 }}>
              A listában még {tovabbiNyitott} nyitott számla van — ha a befizetés azokat is fizeti,
              jelöld ki őket. Túlfizetés csak az legyen, amire már nincs nyitott számla.
            </span>
          )}
        </div>
      )}

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 8, flexWrap: 'wrap' }}>
        <span>
          Kijelölve: <b>{kijelolt.size}</b> db · <b style={{ fontFamily: 'var(--mono)' }}>{ft(osszeg)}</b> Ft
        </span>
        {(kijelolt.size > 0 || vanTulfizetes) && (
          <span style={{ color: kiall ? 'var(--ok)' : 'var(--flag)', fontWeight: 600 }}>
            {kiall
              ? 'kiadja a befizetést'
              : elteres > 0
                ? vanTulfizetes
                  ? kijelolt.size === 0
                    ? `a teljes ${ft(cel)} Ft túlfizetésként kerül a céghez`
                    : `a maradék ${ft(elteres)} Ft túlfizetésként kerül a céghez`
                  : `eltérés +${ft(elteres)} Ft — hiányzó számla?`
                : `a befizetés ${ft(-elteres)} Ft-tal kevesebb — a legújabb számla részben teljesül, a hátralékát a következő utalás zárja le`}
          </span>
        )}
        <span style={{ flex: 1 }}></span>
        <button
          className="btn pri sm"
          disabled={ment || (kijelolt.size === 0 && !(vanTulfizetes && tulCeg.trim()))}
          onClick={() => void rogzit()}
        >
          {ment ? 'Mentés…' : kijelolt.size === 0 ? 'Túlfizetésként rögzítés' : 'Párosítás rögzítése'}
        </button>
        <button className="btn sm" onClick={onMegse}>
          Mégse
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */

function KartyasBontas({
  napok,
  nyitottNap,
  nyitottTomb,
  onNap,
  onTomb,
}: {
  napok: KartyasNap[];
  nyitottNap: string | null;
  nyitottTomb: string | null;
  onNap: (nap: string) => void;
  onTomb: (kulcs: string) => void;
}) {
  if (napok.length === 0) {
    return (
      <div className="uzenet halvany">
        Még nincs kártyás elszámolás. Tölts fel bankkivonatot.
      </div>
    );
  }

  return (
    <div className="matrix" style={{ marginTop: 14 }}>
      <table className="tt2">
        <thead>
          <tr>
            <th>Elfogadás napja / tömb</th>
            <th className="n">Elszámolás</th>
            <th className="n">POS nettó</th>
            <th className="n">Bruttó</th>
            <th className="n">Jutalék</th>
            <th className="n">Kulcs</th>
            <th>Terminál</th>
          </tr>
        </thead>
        <tbody>
          {napok.map((n) => (
            <NapSorok
              key={n.nap}
              nap={n}
              nyitva={nyitottNap === n.nap}
              nyitottTomb={nyitottTomb}
              onNap={onNap}
              onTomb={onTomb}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function NapSorok({
  nap,
  nyitva,
  nyitottTomb,
  onNap,
  onTomb,
}: {
  nap: KartyasNap;
  nyitva: boolean;
  nyitottTomb: string | null;
  onNap: (nap: string) => void;
  onTomb: (kulcs: string) => void;
}) {
  return (
    <>
      {/* Nap szint — összecsukva is látszanak az összegek. */}
      <tr style={{ cursor: 'pointer', background: '#F8F9F7' }} onClick={() => onNap(nap.nap)}>
        <td className="nm3">
          <b>
            {nyitva ? '▾' : '▸'} {nap.nap}
          </b>
        </td>
        <td className="calc2">{nap.elszamolasok}</td>
        <td className="calc2">{ft(nap.netto)}</td>
        <td className="calc2">{ft(nap.brutto)}</td>
        <td className="calc2">{ft(nap.jutalek)}</td>
        <td className="calc2">
          {nap.brutto > 0 ? `${Math.round((nap.jutalek / nap.brutto) * 1000) / 10}%` : '—'}
        </td>
        <td></td>
      </tr>

      {nyitva &&
        nap.tombok.map((t, i) => {
          const kulcs = `${nap.nap}|${t.tomb}|${i}`;
          const tombNyitva = nyitottTomb === kulcs;
          return (
            <Fragment key={kulcs}>
              <tr style={{ cursor: t.szamlak.length > 0 ? 'pointer' : 'default' }} onClick={() => t.szamlak.length > 0 && onTomb(kulcs)}>
                <td style={{ padding: '6px 10px 6px 28px', fontSize: 11.5 }}>
                  {t.szamlak.length > 0 ? (tombNyitva ? '▾ ' : '▸ ') : '   '}
                  {t.tomb === '?' ? (
                    <span className="only" title="Nincs hozzá párosított számlacsoport.">
                      nem párosult
                    </span>
                  ) : (
                    `${t.tomb}. számlatömb · ${t.szamlak.length} számla`
                  )}
                </td>
                <td></td>
                <td className="calc2">{ft(t.pos_netto)}</td>
                <td className="calc2">{ft(t.csoport_brutto)}</td>
                <td className="calc2">{ft(t.jutalek)}</td>
                <td className={`calc2${t.becsult ? ' lejar-hamarosan' : ''}`}>
                  {t.kulcs !== null ? `${t.kulcs}%` : '—'}
                  {t.becsult && <div style={{ fontSize: 10 }}>becsült</div>}
                </td>
                <td className="calc2" style={{ textAlign: 'left' }}>
                  {t.terminal ?? '—'}
                </td>
              </tr>

              {tombNyitva &&
                t.szamlak.map((s) => (
                  <tr key={`${kulcs}-${s.bizonylatszam}`}>
                    <td style={{ padding: '5px 10px 5px 48px', fontSize: 11 }}>
                      <b style={{ fontFamily: 'var(--mono)' }}>{s.bizonylatszam}</b>
                      {s.cegnev && <span style={{ marginLeft: 8 }}>{s.cegnev}</span>}
                    </td>
                    <td colSpan={2}></td>
                    <td className="calc2">{ft(s.brutto)}</td>
                    <td colSpan={3}></td>
                  </tr>
                ))}
            </Fragment>
          );
        })}
    </>
  );
}
